import { useCallback, useEffect, useRef, useState } from "react";
import config from "@/config";
import type { DayCall } from "@/types";

const TZ = "America/New_York";

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

function mapCall(raw: Record<string, unknown>): DayCall {
  const duration = raw.duration;
  return {
    caseId: String(raw.case_id ?? ""),
    callId: String(raw.call_id ?? ""),
    attemptIndex: Number(raw.attempt_index ?? 0),
    totalAttempts: Number(raw.total_attempts ?? 0),
    timestamp: String(raw.timestamp ?? ""),
    success: String(raw.success ?? ""),
    paStatus: raw.pa_status ? String(raw.pa_status) : undefined,
    failReason: raw.fail_reason ? String(raw.fail_reason) : undefined,
    duration: typeof duration === "number" ? duration : undefined,
    productName: raw.product_name ? String(raw.product_name) : undefined,
    payerName: raw.payer_name ? String(raw.payer_name) : undefined,
    memberFirstName: raw.member_first_name ? String(raw.member_first_name) : undefined,
    memberLastName: raw.member_last_name ? String(raw.member_last_name) : undefined,
  };
}

export function useDayCalls(date: string, enabled: boolean) {
  const [calls, setCalls] = useState<DayCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    setCalls([]);
    setTruncated(false);
    try {
      const params = new URLSearchParams({ date, tz: TZ });
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/day-calls?${params.toString()}`,
        { signal: ac.signal },
      );
      if (!res.ok) {
        let detail = `Could not load calls (${res.status})`;
        try {
          const body = (await res.json()) as { detail?: string };
          if (body.detail) detail = body.detail;
        } catch {
          /* keep status text */
        }
        throw new Error(detail);
      }
      const data = (await res.json()) as {
        calls?: Record<string, unknown>[];
        truncated?: boolean;
      };
      if (seq !== seqRef.current) return;
      setCalls((data.calls ?? []).map(mapCall));
      setTruncated(Boolean(data.truncated));
    } catch (err) {
      if (seq !== seqRef.current) return;
      if (isAbortError(err)) return;
      setCalls([]);
      setTruncated(false);
      setError(err instanceof Error ? err.message : "Could not load calls");
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, load]);

  return { calls, loading, error, truncated, refresh: load };
}
